/**
 * supabaseApi.js — the RazerGrid backend, moved into the browser.
 *
 * WHAT THIS IS
 * Every backend call in App.jsx funnels through one function:
 *   apiCall(method, path, body)   (useVistaServer, ~line 21441)
 * This module implements the other side of that call. Each route below
 * reproduces the response shape of the Express endpoint it replaces, so the
 * ~40 wrapper functions and ~120 call sites above apiCall never had to change.
 *
 * PARITY RULES (the whole point of this file)
 *  - Return exactly what the Express route returned, including field names and
 *    casing (`part_id` vs `partId` is inconsistent in the original API — that
 *    inconsistency is preserved deliberately).
 *  - Return `null` on any failure. The old apiCall returned null for every
 *    non-2xx response, and callers are written against that.
 *  - Never let a >1000-row table truncate. PostgREST caps a response at 1000
 *    rows by default and does not warn; `selectAll` pages through instead.
 *    grading_reference (1.7k) and repair_centre_data (25k) both exceed it, and
 *    a silent truncation here means wrong commission totals.
 */

import { supabase } from "./supabaseClient";
import { sanitizeTeamTargets } from "./teamTargets";
import {
  getEffectiveGradingTeams,
  normalizeGradingTeams,
} from "./gradingTeams";

const PAGE = 1000;

// ---------------------------------------------------------------------------
// Query helpers
// ---------------------------------------------------------------------------

/**
 * Paginated select. `apply` receives the query builder and returns it with
 * filters/order applied. Pages until a short page comes back.
 */
async function selectAll(table, apply, columns = "*") {
  const out = [];
  for (let from = 0; ; from += PAGE) {
    let q = supabase.from(table).select(columns);
    if (apply) q = apply(q);
    const { data, error } = await q.range(from, from + PAGE - 1);
    if (error) throw error;
    out.push(...(data || []));
    if (!data || data.length < PAGE) break;
  }
  return out;
}

async function rpc(fn, params) {
  const { data, error } = await supabase.rpc(fn, params || {});
  if (error) throw error;
  return data;
}

/** RPCs that return jsonb use `{error: "..."}` where the server sent a 4xx. */
function unwrap(result) {
  if (
    result &&
    typeof result === "object" &&
    !Array.isArray(result) &&
    (result.error || result.ok === false)
  ) {
    return null;
  }
  return result;
}

function jsonParse(value, fallback) {
  try {
    const parsed = JSON.parse(value);
    return parsed === null || parsed === undefined ? fallback : parsed;
  } catch {
    return fallback;
  }
}

const norm = (s) => String(s || "").toLowerCase().replace(/[^a-z]/g, "");
const cleanPartId = (v) => String(v || "").trim().toUpperCase();
const MONTH_KEY_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

function isMonthKey(value) {
  return MONTH_KEY_RE.test(String(value || ""));
}

function monthKeyParts(value) {
  if (!isMonthKey(value)) return null;
  const [year, month] = String(value).split("-").map((v) => parseInt(v, 10));
  return { year, month };
}

function compareMonthKeys(a, b) {
  return String(a || "").localeCompare(String(b || ""));
}

function expandMonthRange(from, to) {
  const start = monthKeyParts(from);
  const end = monthKeyParts(to);
  if (!start || !end || compareMonthKeys(from, to) > 0) return [];
  const out = [];
  let year = start.year;
  let month = start.month;
  while (year < end.year || (year === end.year && month <= end.month)) {
    out.push(`${year}-${String(month).padStart(2, "0")}`);
    month += 1;
    if (month > 12) {
      month = 1;
      year += 1;
    }
  }
  return out;
}

function lastDateOfMonthKey(value) {
  const parts = monthKeyParts(value);
  if (!parts) return "";
  const day = new Date(parts.year, parts.month, 0).getDate();
  return `${value}-${String(day).padStart(2, "0")}`;
}

function emptyHistoryPack(month) {
  return {
    month,
    exists: false,
    summaries: [],
    records: [],
    rota: [],
    bankHolidays: [],
  };
}

function settingTimestamp() {
  return new Date().toISOString().replace("T", " ").slice(0, 19);
}

async function fetchTeamTargetsSetting() {
  const rows = await selectAll(
    "app_settings",
    (q) => q.eq("key", "team_targets"),
    "key,value_json",
  );
  return {
    teamTargets: sanitizeTeamTargets(rows[0]?.value_json || {}),
  };
}

async function saveTeamTargetsSetting(body) {
  const clean = sanitizeTeamTargets(body?.teamTargets || body || {});
  const { error } = await supabase.from("app_settings").upsert(
    {
      key: "team_targets",
      value_json: clean,
      updated_at: settingTimestamp(),
    },
    { onConflict: "key" },
  );
  if (error) return null;
  return { ok: true, teamTargets: clean };
}

// ---------------------------------------------------------------------------
// Shared shapes
// ---------------------------------------------------------------------------

/** Mirrors the server's serializeAllocationBlocklist(). */
async function serializeBlocklist() {
  const rows = await selectAll("allocation_part_blocklist", (q) =>
    q.order("scope").order("engineer").order("part_id"),
    "scope,engineer,part_id,updated_at"
  );
  const global = [];
  const engineers = {};
  for (const r of rows) {
    if (r.scope === "engineer") {
      const eng = String(r.engineer || "").trim();
      if (!eng) continue;
      if (!engineers[eng]) engineers[eng] = [];
      engineers[eng].push({ partId: r.part_id, updatedAt: r.updated_at });
    } else {
      global.push({ partId: r.part_id, updatedAt: r.updated_at });
    }
  }
  return { global, engineers, rows };
}

/** Mirrors GET /api/grading — reference grades with overrides folded in. */
async function gradingRows(team, excluded) {
  // Filtered in JS rather than PostgREST. The original predicates mix
  // `IS NULL OR = ''` across three columns, which is fiddly and easy to get
  // subtly wrong in .or() syntax; the table is ~1.7k rows so the difference is
  // not worth the risk of a wrong exclusion silently changing commission.
  const rows = await selectAll("grading_reference", null, "part_id,grade,team,excluded,silent_exclude");
  const manualTeamRows = await selectAll(
    "grading_part_teams",
    null,
    "part_id,team",
  );
  const teamsByPart = {};
  for (const row of manualTeamRows) {
    const partId = cleanPartId(row.part_id);
    if (!partId) continue;
    if (!teamsByPart[partId]) teamsByPart[partId] = [];
    teamsByPart[partId].push(row.team);
  }

  const filtered = rows.filter((r) => {
    if (team && team !== "ALL") {
      const effectiveTeams = getEffectiveGradingTeams({
        team: r.team,
        teams: teamsByPart[cleanPartId(r.part_id)] || [],
      });
      if (effectiveTeams.length && !effectiveTeams.includes(String(team).trim().toUpperCase())) {
        return false;
      }
    }
    const isExcluded = r.excluded === 1;
    const isSilent = r.silent_exclude === 1;
    if (excluded === "0") return !isExcluded && !isSilent;
    if (excluded === "1") return isExcluded && !isSilent;
    return true;
  });

  const overrides = await selectAll("grade_overrides", null, "part_id,grade");
  const map = {};
  for (const o of overrides) map[o.part_id] = o.grade;

  // The server selected only these four columns — keep the shape identical.
  // `teams` is the effective list (manual, else the legacy single team).
  // `manual_teams` is the raw Parts Grading assignment with no fallback, which
  // is what allocation and the Team Access views need: only an explicit tick
  // may widen which team can fix a part.
  return filtered.map((r) => {
    const manualTeams = teamsByPart[cleanPartId(r.part_id)] || [];
    return {
      teams: getEffectiveGradingTeams({ team: r.team, teams: manualTeams }),
      manual_teams: normalizeGradingTeams(manualTeams),
      part_id: r.part_id,
      grade: map[r.part_id] ? map[r.part_id] : r.grade,
      team: r.team,
      excluded: r.excluded,
    };
  });
}

/**
 * Mirror a Parts Grading team assignment into the team part reference lists.
 *
 * Two stores could answer "may this team fix this client part?", and letting
 * both answer is what broke allocation: the allocation merge learned to honour
 * the Parts Grading tick while the call-list parser carried on reading only
 * team_part_references, so the same part was allocatable but uncallable.
 *
 * So the tick is written into the reference list and the reference list stays
 * the single gate. Only client-prefixed stock has a list at all — team stock is
 * gated by the grading team, so there is nothing to mirror for it.
 *
 * Add-only, deliberately. A part with no manual rows shows its legacy owning
 * team in the Parts Grading form, so a plain grade edit would otherwise delete
 * a team off a list it was uploaded onto. Removing a team from a reference list
 * stays a job for the reference upload screen.
 */
async function mirrorTeamsToPartReferences(partId, teams) {
  const key = cleanPartId(partId);
  const wanted = normalizeGradingTeams(teams);
  if (!key || !wanted.length) return;

  const clients = await selectAll("clients", (q) => q.eq("active", 1), "id,prefix");
  const match = (clients || []).find((c) => {
    const prefix = cleanPartId(c.prefix);
    return prefix && key.startsWith(prefix);
  });
  if (!match) return;

  const { error } = await supabase.from("team_part_references").upsert(
    wanted.map((team) => ({ team, client_id: match.id, part_id: key })),
    { onConflict: "team,client_id,part_id", ignoreDuplicates: true },
  );
  // A failure here leaves the grade saved and the list unchanged, which is the
  // state the part was already in. Surfacing it would abort a save that worked.
  if (error) console.warn("[grading] part reference mirror failed:", error.message);
}

/** Mirrors GET /api/engineers — roster built from rota + aliases. */
async function engineerRoster() {
  const rota = await selectAll("rota_entries", (q) =>
    q.order("year", { ascending: false })
     .order("month", { ascending: false })
     .order("uploaded_at", { ascending: false }),
    "engineer,section,year,month,uploaded_at"
  );

  const roster = {};
  for (const r of rota) {
    const key = norm(r.engineer);
    if (!key || roster[key]) continue;
    roster[key] = {
      engineer: r.engineer,
      section: r.section || "",
      year: r.year,
      month: r.month,
      uploadedAt: r.uploaded_at,
      aliases: [],
      status: "active",
      statusNote: "",
    };
  }

  // New starters the rota does not list yet. Matched on the same letters-only
  // key, so "LUCASPAYNE" here merges with "Lucas Payne" once the rota has him.
  const added = await selectAll("engineer_roster", (q) =>
    q.order("engineer"),
    "engineer,section,source,added_at"
  );
  for (const r of added) {
    const key = norm(r.engineer);
    if (!key) continue;
    if (roster[key]) {
      roster[key].addedSource = r.source || "manual";
      continue;
    }
    roster[key] = {
      engineer: r.engineer,
      section: r.section || "",
      year: null,
      month: null,
      uploadedAt: r.added_at,
      aliases: [],
      status: "active",
      statusNote: "",
      addedSource: r.source || "manual",
    };
  }

  const aliases = await selectAll("engineer_aliases", (q) =>
    q.order("engineer").order("alias"),
    "id,engineer,alias,updated_at"
  );
  for (const a of aliases) {
    const key = norm(a.engineer);
    if (!key) continue;
    if (!roster[key]) {
      roster[key] = {
        engineer: a.engineer, section: "", year: null,
        month: null, uploadedAt: null, aliases: [],
        status: "active", statusNote: "",
      };
    }
    roster[key].aliases.push({ id: a.id, alias: a.alias, updatedAt: a.updated_at });
  }

  const statuses = await selectAll("engineer_status", (q) =>
    q.order("engineer"),
    "engineer,status,note,updated_at"
  );
  for (const s of statuses) {
    const key = norm(s.engineer);
    if (!key) continue;
    // Keep status-only engineers visible so they can be reinstated later.
    if (!roster[key]) {
      roster[key] = {
        engineer: s.engineer, section: "", year: null,
        month: null, uploadedAt: null, aliases: [],
        status: "active", statusNote: "",
      };
    }
    roster[key].status = s.status || "active";
    roster[key].statusNote = s.note || "";
  }

  return Object.values(roster).sort((a, b) =>
    String(a.engineer).localeCompare(String(b.engineer))
  );
}

async function historyRangePack(from, to, monthKeys = expandMonthRange(from, to)) {
  const label = `${from || "?"} to ${to || "?"}`;
  if (!monthKeys.length) return emptyHistoryPack(label);
  const monthSet = new Set(monthKeys);
  const firstMonth = monthKeyParts(monthKeys[0]);
  const lastMonth = monthKeyParts(monthKeys[monthKeys.length - 1]);

  const summaries = await selectAll("monthly_summaries", (q) =>
    q.gte("month", from).lte("month", to).order("month", { ascending: false })
      .order("total_commission", { ascending: false }),
    "month,engineer,team,total_parts,total_commission,bonus_earned,avg_per_day,days_worked,created_at"
  );

  const records = await selectAll("repair_centre_data", (q) =>
    q.gte("month", from).lte("month", to).order("month", { ascending: false })
      .order("completed_at", { ascending: true })
      .order("engineer", { ascending: true })
      .order("part_id", { ascending: true }),
    "month,engineer,team,part_id,grade,commission,is_ber,unit_status,completed_at,source_completed_at,bonus_eligible,upload_date,created_at"
  );

  const rotaRows = await selectAll("rota_entries", (q) =>
    q.gte("year", firstMonth.year).lte("year", lastMonth.year)
      .order("year", { ascending: true }).order("month", { ascending: true })
      .order("engineer", { ascending: true }),
    "year,month,engineer,section,days_json,dow_json,hols_full,hols_half,hols_total,uploaded_at"
  );
  const rota = rotaRows
    .filter((row) => monthSet.has(`${row.year}-${String(row.month).padStart(2, "0")}`))
    .map((row) => ({
      ...row,
      month_key: `${row.year}-${String(row.month).padStart(2, "0")}`,
    }));

  const holidayRows = await selectAll("bank_holidays", (q) =>
    q.gte("holiday_date", `${from}-01`).lte("holiday_date", lastDateOfMonthKey(to))
      .order("holiday_date", { ascending: true }),
    "holiday_date,name,active,year"
  );
  const seenHoliday = new Set();
  const bankHolidays = holidayRows.filter((h) => {
    const key = `${h.holiday_date}|${h.name}`;
    const holidayMonth = String(h.holiday_date || "").slice(0, 7);
    const isActive = h.active === 1 || h.active === true;
    if (!isActive || !monthSet.has(holidayMonth) || seenHoliday.has(key)) return false;
    seenHoliday.add(key);
    return true;
  });

  return {
    month: label,
    exists: summaries.length > 0 || records.length > 0,
    summaries,
    records,
    rota,
    bankHolidays,
  };
}

async function historyRangePayload(from, to) {
  const monthKeys = expandMonthRange(from, to);
  if (!monthKeys.length) return null;
  const months = await rpc("rg_history_months");
  const label = `${from || "?"} to ${to || "?"}`;
  return {
    months: (months || []).map((r) => ({
      month: r.month,
      summary_count: Number(r.summary_count),
      record_count: Number(r.record_count),
    })),
    selectedMonth: label,
    compareMonth: "",
    current: await historyRangePack(from, to, monthKeys),
    compare: emptyHistoryPack(""),
  };
}

// ---------------------------------------------------------------------------
// Routes
//
// Order matters: literal segments must be registered before parameterised ones
// that would also match. `/api/progress/flags` has the same segment count as
// `/api/progress/:date`, and `/api/grading/count` as `/api/grading/:partId`.
// ---------------------------------------------------------------------------

const ROUTES = [
  // --- health -------------------------------------------------------------
  ["GET", "/api/health", async () => ({
    status: "ok",
    timestamp: new Date().toISOString(),
    version: "4.0.0-supabase",
  })],

  // --- auth ---------------------------------------------------------------
  // Not via selectAll: its .range() would override the .limit() below.
  ["GET", "/api/auth/last-login", async () => {
    const { data, error } = await supabase
      .from("auth_log")
      .select("type,logged_in_at")
      .order("id", { ascending: false })
      .limit(1);
    if (error) throw error;
    if (!data || !data.length) return { lastLogin: null };
    return { lastLogin: data[0].logged_in_at, type: data[0].type };
  }],

  ["POST", "/api/auth/change-password", async (_p, _q, body) => {
    const next = body && body.newPassword;
    if (typeof next !== "string" || next.length < 8) return null;
    const { error } = await supabase.auth.updateUser({ password: next });
    if (error) return null;
    return { ok: true };
  }],

  ["GET", "/api/team-targets", async () => fetchTeamTargetsSetting()],

  ["PUT", "/api/team-targets", async (_p, _q, body) =>
    saveTeamTargetsSetting(body)],

  // --- grading ------------------------------------------------------------
  ["POST", "/api/grading/upload", async (_p, _q, body) =>
    unwrap(await rpc("rg_grading_upload", { p_records: (body && body.records) || [] }))],

  ["GET", "/api/grading/count", async () => {
    const rows = await rpc("rg_grading_count");
    const r = Array.isArray(rows) ? rows[0] : rows;
    return { total: Number(r?.total || 0), overrides: Number(r?.overrides || 0) };
  }],

  ["GET", "/api/grading/overrides", async () =>
    selectAll("grade_overrides", (q) => q.order("updated_at", { ascending: false }))],

  ["POST", "/api/grading/override", async (_p, _q, body) =>
    unwrap(await rpc("rg_grade_override", {
      p_part_id: body && body.part_id,
      p_grade: body && body.grade,
    }))],

  ["POST", "/api/grading/override-with-teams", async (_p, _q, body) => {
    const teams = Array.isArray(body && body.teams) ? body.teams : [];
    const result = unwrap(await rpc("rg_grade_override_with_teams", {
      p_part_id: body && body.part_id,
      p_grade: body && body.grade,
      p_teams: teams,
    }));
    // The tick is only half-made until the team part reference list knows about
    // it: that list is what allocation and the call list both read.
    if (result && result.ok) {
      await mirrorTeamsToPartReferences(body && body.part_id, teams);
    }
    return result;
  }],

  ["POST", "/api/grading/exclude", async (_p, _q, body) =>
    unwrap(await rpc("rg_grading_exclude", { p_part_id: body && body.part_id }))],

  ["POST", "/api/grading/restore", async (_p, _q, body) =>
    unwrap(await rpc("rg_grading_restore", { p_part_id: body && body.part_id }))],

  ["GET", "/api/grading", async (_p, query) =>
    gradingRows(query.get("team"), query.get("excluded"))],

  ["DELETE", "/api/grading/:partId", async (params) =>
    unwrap(await rpc("rg_grading_delete", { p_part_id: params.partId }))],

  // --- bank holidays ------------------------------------------------------
  ["GET", "/api/bank-holidays/all", async () =>
    selectAll("bank_holidays", (q) => q.order("holiday_date"))],

  ["GET", "/api/bank-holidays", async (_p, query) => {
    const year = query.get("year");
    return selectAll("bank_holidays", (q) => {
      let b = q.eq("active", 1);
      if (year) b = b.eq("year", parseInt(year, 10));
      return b.order("holiday_date");
    });
  }],

  ["POST", "/api/bank-holidays/toggle", async (_p, _q, body) => {
    const { error } = await supabase
      .from("bank_holidays")
      .update({ active: body && body.active ? 1 : 0 })
      .eq("id", body && body.id);
    if (error) return null;
    return { ok: true };
  }],

  ["POST", "/api/bank-holidays/add", async (_p, _q, body) => {
    if (!body || !body.date || !body.name) return null;
    const { error } = await supabase.from("bank_holidays").upsert(
      {
        holiday_date: body.date,
        name: body.name,
        year: parseInt(String(body.date).split("-")[0], 10),
        is_custom: 1,
      },
      { onConflict: "holiday_date", ignoreDuplicates: true }
    );
    if (error) return null;
    return { ok: true };
  }],

  ["DELETE", "/api/bank-holidays/:id", async (params) => {
    const { error } = await supabase
      .from("bank_holidays")
      .delete()
      .eq("id", parseInt(params.id, 10))
      .eq("is_custom", 1);
    if (error) return null;
    return { ok: true };
  }],

  // --- rota ---------------------------------------------------------------
  ["GET", "/api/rota/months", async () => {
    const rows = await rpc("rg_rota_months");
    return (rows || []).map((r) => ({
      year: r.year, month: r.month,
      engineer_count: Number(r.engineer_count),
      uploaded_at: r.uploaded_at,
    }));
  }],

  ["GET", "/api/rota/public/:year/:month/:engineer", async (params) =>
    rpc("rg_public_rota", {
      p_year: parseInt(params.year, 10),
      p_month: parseInt(params.month, 10),
      p_engineer: params.engineer,
    })],

  ["POST", "/api/rota/upload", async (_p, _q, body) => {
    if (!body || !body.year || !body.month ||
        !Array.isArray(body.engineers) || !Array.isArray(body.dow)) return null;
    return unwrap(await rpc("rg_rota_upload", {
      p_year: parseInt(body.year, 10),
      p_month: parseInt(body.month, 10),
      p_dow: body.dow,
      p_engineers: body.engineers,
    }));
  }],

  ["GET", "/api/rota/:year/:month", async (params) =>
    selectAll("rota_entries", (q) =>
      q.eq("year", parseInt(params.year, 10)).eq("month", parseInt(params.month, 10)))],

  ["DELETE", "/api/rota/:year/:month", async (params) => {
    const { error } = await supabase
      .from("rota_entries").delete()
      .eq("year", parseInt(params.year, 10))
      .eq("month", parseInt(params.month, 10));
    if (error) return null;
    return { ok: true };
  }],

  // --- engineers ----------------------------------------------------------
  ["GET", "/api/engineers", async () => engineerRoster()],

  ["POST", "/api/engineers", async (_p, _q, body) =>
    unwrap(await rpc("rg_add_engineer", {
      p_engineer: (body && body.engineer) || "",
      p_section: (body && body.section) || "",
      p_source: (body && body.source) || "manual",
      p_note: (body && body.note) || "",
    }))],

  ["DELETE", "/api/engineers/:engineer", async (params) =>
    unwrap(await rpc("rg_remove_engineer", { p_engineer: params.engineer }))],

  ["PUT", "/api/engineers/:engineer/aliases", async (params, _q, body) =>
    unwrap(await rpc("rg_set_engineer_aliases", {
      p_engineer: params.engineer,
      p_aliases: (body && Array.isArray(body.aliases)) ? body.aliases : [],
    }))],

  ["PUT", "/api/engineers/:engineer/status", async (params, _q, body) =>
    unwrap(await rpc("rg_set_engineer_status", {
      p_engineer: params.engineer,
      p_status: (body && body.status) || "active",
      p_note: (body && body.note) || "",
    }))],

  // --- allocation blocklist -----------------------------------------------
  ["GET", "/api/allocation-blocklist", async () => serializeBlocklist()],

  ["POST", "/api/allocation-blocklist", async (_p, _q, body) => {
    const b = body || {};
    const scope = b.scope === "engineer" ? "engineer" : "global";
    const engineer = scope === "engineer" ? String(b.engineer || "").trim() : "";
    const ids = Array.isArray(b.partIds) ? b.partIds : [b.partId];

    const clean = [];
    for (const id of ids) {
      const partId = cleanPartId(id);
      if (partId && !clean.includes(partId)) clean.push(partId);
    }
    if (scope === "engineer" && !engineer) return null;
    if (clean.length === 0) return null;

    const { error } = await supabase.from("allocation_part_blocklist").upsert(
      clean.map((part_id) => ({ scope, engineer, part_id })),
      { onConflict: "scope,engineer,part_id" }
    );
    if (error) return null;
    return { ok: true, inserted: clean.length, blocklist: await serializeBlocklist() };
  }],

  ["PUT", "/api/allocation-blocklist", async (_p, _q, body) => {
    const b = body || {};
    const res = unwrap(await rpc("rg_blocklist_replace", {
      p_global: Array.isArray(b.global) ? b.global : [],
      p_engineers: (b.engineers && typeof b.engineers === "object") ? b.engineers : {},
    }));
    if (!res) return null;
    return { ok: true, blocklist: await serializeBlocklist() };
  }],

  ["DELETE", "/api/allocation-blocklist", async (_p, query) => {
    const scope = query.get("scope") === "engineer" ? "engineer" : "global";
    const engineer = scope === "engineer" ? String(query.get("engineer") || "").trim() : "";
    const partId = cleanPartId(query.get("partId"));
    if (!partId) return null;
    if (scope === "engineer" && !engineer) return null;

    const { error } = await supabase
      .from("allocation_part_blocklist").delete()
      .eq("scope", scope).eq("engineer", engineer).eq("part_id", partId);
    if (error) return null;
    return { ok: true, blocklist: await serializeBlocklist() };
  }],

  // --- engineer progress --------------------------------------------------
  ["POST", "/api/progress/sync", async (_p, _q, body) => {
    const b = body || {};
    if (!b.shareDate || !b.engineer || !Array.isArray(b.completed)) return null;
    return unwrap(await rpc("rg_public_progress_sync", {
      p_share_date: b.shareDate,
      p_engineer: b.engineer,
      p_day_number: b.dayNumber == null ? null : b.dayNumber,
      p_completed: b.completed,
      p_parts: b.parts || [],
      p_grade_values: b.gradeValues || {},
      p_tiers: b.tiers || [],
    }));
  }],

  ["POST", "/api/progress/flag", async (_p, _q, body) => {
    const b = body || {};
    return unwrap(await rpc("rg_public_flag_grade", {
      p_share_date: b.shareDate,
      p_engineer: b.engineer,
      p_part_serial: b.partSerial,
      p_part_id: b.partId,
      p_current_grade: b.currentGrade,
      p_suggested_grade: b.suggestedGrade,
      p_notes: b.notes,
    }));
  }],

  ["GET", "/api/progress/flags", async (_p, query) => {
    const status = query.get("status");
    return selectAll("grade_flags", (q) => {
      let b = q;
      if (status) b = b.eq("status", status);
      return b.order("created_at", { ascending: false });
    });
  }],

  ["PUT", "/api/progress/flags/:id", async (params, _q, body) => {
    if (!body || !body.status) return null;
    return unwrap(await rpc("rg_flag_resolve", {
      p_id: parseInt(params.id, 10),
      p_status: body.status,
      p_apply_override: !!body.applyOverride,
    }));
  }],

  ["GET", "/api/progress/:date", async (params) => {
    const rows = await selectAll("engineer_progress", (q) =>
      q.eq("share_date", params.date).order("last_updated", { ascending: false }));
    return rows.map((r) => ({
      id: r.id,
      shareDate: r.share_date,
      engineer: r.engineer,
      dayNumber: r.day_number,
      completed: jsonParse(r.completed_json, []),
      parts: jsonParse(r.parts_json, []),
      gradeValues: jsonParse(r.grade_values_json, {}),
      tiers: jsonParse(r.tiers_json, []),
      lastUpdated: r.last_updated,
    }));
  }],

  ["DELETE", "/api/progress/:date/:engineer", async (params) => {
    const { data, error } = await supabase
      .from("engineer_progress").delete()
      .eq("share_date", params.date).eq("engineer", params.engineer)
      .select("id");
    if (error) return null;
    return { ok: true, deleted: (data || []).length };
  }],

  // --- workdays -----------------------------------------------------------
  ["GET", "/api/engineer-workdays/:engineer/:month", async (params) => {
    const rows = await rpc("rg_engineer_workdays", {
      p_engineer: params.engineer,
      p_month: params.month,
    });
    const dates = (rows || []).map((r) => r.work_date);
    return {
      engineer: params.engineer,
      month: params.month,
      days_worked: dates.length,
      dates,
    };
  }],

  ["GET", "/api/working-days-left", async () => {
    // Pure date arithmetic — the server had no reason to own this.
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const lastDay = new Date(year, month + 1, 0).getDate();
    const today = now.getDate();

    const holidays = await selectAll("bank_holidays", (q) =>
      q.eq("active", 1).eq("year", year), "holiday_date");
    const hs = new Set(holidays.map((h) => h.holiday_date));

    let count = 0;
    for (let d = today + 1; d <= lastDay; d++) {
      const date = new Date(year, month, d);
      const dow = date.getDay();
      const ds = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      if (dow !== 0 && dow !== 6 && !hs.has(ds)) count++;
    }
    const iso = (dt) =>
      `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
    return {
      working_days_left: count,
      today: iso(now),
      month_end: iso(new Date(year, month + 1, 0)),
    };
  }],

  // --- allocations / completions -----------------------------------------
  ["POST", "/api/allocations", async (_p, _q, body) => {
    const b = body || {};
    if (!Array.isArray(b.parts)) return null;
    const { error } = await supabase.from("daily_allocations").insert({
      date: b.date, engineer: b.engineer, team: b.team,
      parts_json: JSON.stringify(b.parts),
      total_parts: b.parts.length,
      total_commission: b.totalCommission,
    });
    if (error) return null;
    return { ok: true };
  }],

  ["GET", "/api/allocations", async (_p, query) =>
    selectAll("daily_allocations", (q) => {
      let b = q;
      if (query.get("from")) b = b.gte("date", query.get("from"));
      if (query.get("to")) b = b.lte("date", query.get("to"));
      if (query.get("engineer")) b = b.eq("engineer", query.get("engineer"));
      if (query.get("team")) b = b.eq("team", query.get("team"));
      return b.order("date", { ascending: false });
    })],

  ["POST", "/api/completions", async (_p, _q, body) => {
    const b = body || {};
    if (!b.date || !b.engineer || !b.partId || !b.grade) return null;
    const { error } = await supabase.from("completions").insert({
      date: b.date, engineer: b.engineer, part_id: b.partId,
      grade: b.grade, commission: b.commission,
    });
    if (error) return null;
    return { ok: true };
  }],

  ["GET", "/api/completions/:engineer/:date", async (params) =>
    selectAll("completions", (q) =>
      q.eq("engineer", params.engineer).eq("date", params.date).order("completed_at"))],

  // --- summaries / reports ------------------------------------------------
  ["POST", "/api/summaries", async (_p, _q, body) =>
    unwrap(await rpc("rg_summary_save", { p_summary: body || {} }))],

  ["GET", "/api/summaries", async (_p, query) =>
    selectAll("monthly_summaries", (q) => {
      let b = q;
      if (query.get("month")) b = b.eq("month", query.get("month"));
      if (query.get("team")) b = b.eq("team", query.get("team"));
      return b.order("total_commission", { ascending: false });
    })],

  ["GET", "/api/leaderboard/:month", async (params) => {
    const rows = await rpc("rg_leaderboard", { p_month: params.month });
    return (rows || []).map((r) => ({
      engineer: r.engineer, team: r.team,
      parts: Number(r.parts), commission: r.commission,
      bonus: r.bonus, avg_daily: r.avg_daily,
    }));
  }],

  ["POST", "/api/repair-data", async (_p, _q, body) => {
    const b = body || {};
    if (!b.month || !Array.isArray(b.records)) return null;
    return unwrap(await rpc("rg_repair_data_replace", {
      p_month: b.month,
      p_upload_date: b.uploadDate,
      p_records: b.records,
    }));
  }],

  ["GET", "/api/repair-data", async (_p, query) =>
    selectAll("repair_centre_data", (q) => {
      let b = q;
      if (query.get("month")) b = b.eq("month", query.get("month"));
      if (query.get("team")) b = b.eq("team", query.get("team"));
      if (query.get("engineer")) b = b.eq("engineer", query.get("engineer"));
      return b.order("created_at", { ascending: false });
    })],

  ["GET", "/api/reports/team-overview/:month", async (params) => {
    const rows = await rpc("rg_team_overview", { p_month: params.month });
    return (rows || []).map((r) => ({
      team: r.team,
      engineer_count: Number(r.engineer_count),
      total_parts: Number(r.total_parts),
      total_commission: r.total_commission,
      avg_daily: r.avg_daily,
    }));
  }],

  // Not via selectAll: its .range() would override this .limit(12), which the
  // original endpoint relied on to cap the history at 12 months.
  ["GET", "/api/reports/engineer-history/:engineer", async (params) => {
    const { data, error } = await supabase
      .from("monthly_summaries")
      .select("month,total_parts,total_commission,bonus_earned,avg_per_day,days_worked")
      .eq("engineer", params.engineer)
      .order("month", { ascending: false })
      .limit(12);
    if (error) throw error;
    return data || [];
  }],

  ["GET", "/api/reports/engineer-progress-public/:month/:engineer", async (params) =>
    rpc("rg_public_engineer_progress", {
      p_month: params.month,
      p_engineer: params.engineer,
    })],

  ["GET", "/api/history/months", async () => {
    const rows = await rpc("rg_history_months");
    return (rows || []).map((r) => ({
      month: r.month,
      summary_count: Number(r.summary_count),
      record_count: Number(r.record_count),
    }));
  }],

  ["GET", "/api/history/repair-centre", async (_p, query) => {
    const from = query.get("from") || "";
    const to = query.get("to") || "";
    if (from || to) return historyRangePayload(from, to);
    return rpc("rg_history_payload", {
      p_month: query.get("month") || "",
      p_compare: query.get("compare") || "",
    });
  }],

  // --- stock clients ------------------------------------------------------
  ["GET", "/api/clients", async () => rpc("rg_public_clients")],

  ["POST", "/api/clients", async (_p, _q, body) => {
    const b = body || {};
    if (!b.id || !b.name || !b.prefix) return null;
    const { error } = await supabase.from("clients").upsert(
      { id: b.id, name: b.name, prefix: b.prefix, color: b.color || null, active: 1 },
      { onConflict: "id" }
    );
    if (error) return null;
    return { ok: true };
  }],

  ["DELETE", "/api/clients/:id", async (params) =>
    unwrap(await rpc("rg_delete_client", { p_id: params.id }))],

  // --- team part references -----------------------------------------------
  ["POST", "/api/part-references/upload", async (_p, _q, body) => {
    const b = body || {};
    if (!b.team || !b.clientId || !Array.isArray(b.partIds)) return null;
    return unwrap(await rpc("rg_part_references_upload", {
      p_team: b.team, p_client_id: b.clientId, p_part_ids: b.partIds,
    }));
  }],

  ["DELETE", "/api/part-references/:team/:clientId", async (params) => {
    const { error } = await supabase
      .from("team_part_references").delete()
      .eq("team", params.team).eq("client_id", params.clientId);
    if (error) return null;
    return { ok: true };
  }],

  ["GET", "/api/part-references/:team/:clientId", async (params) => {
    const rows = await selectAll("team_part_references", (q) =>
      q.eq("team", params.team).eq("client_id", params.clientId), "part_id");
    return { partIds: rows.map((r) => r.part_id), count: rows.length };
  }],

  ["GET", "/api/part-references/:team", async (params) => {
    const rows = await rpc("rg_part_reference_counts", { p_team: params.team });
    return (rows || []).map((r) => ({ client_id: r.client_id, count: Number(r.count) }));
  }],

  // Single-part edits to one team's allowlist. The upload route replaces the
  // whole list, which is the wrong tool for correcting one wrong entry — and a
  // wrong entry is invisible in the day it produces, so it has to be fixable
  // without re-uploading a file.
  //
  // Both take the part id in the body rather than the path: part ids contain
  // slashes (MCD-CAB-RJ45/9W-PAN), which no amount of escaping survives a
  // path-segment match.
  ["POST", "/api/part-references/add", async (_p, _q, body) => {
    const b = body || {};
    const partId = cleanPartId(b.partId);
    if (!b.team || !b.clientId || !partId) return null;
    const { error } = await supabase.from("team_part_references").upsert(
      [{ team: b.team, client_id: b.clientId, part_id: partId }],
      { onConflict: "team,client_id,part_id", ignoreDuplicates: true },
    );
    if (error) return { ok: false, error: error.message };
    return { ok: true, partId };
  }],

  ["POST", "/api/part-references/remove", async (_p, _q, body) => {
    const b = body || {};
    const partId = cleanPartId(b.partId);
    if (!b.team || !b.clientId || !partId) return null;
    const { error } = await supabase
      .from("team_part_references").delete()
      .eq("team", b.team).eq("client_id", b.clientId).eq("part_id", partId);
    if (error) return { ok: false, error: error.message };
    return { ok: true, partId };
  }],

  // Every team's client allowlist in one read. Used by the Team Access view
  // and the cross-team History report, which both need the whole picture
  // rather than one team at a time.
  ["GET", "/api/part-references", async () => {
    const rows = await selectAll("team_part_references", null,
      "team,client_id,part_id");
    return rows.map((r) => ({
      team: r.team, client_id: r.client_id, part_id: r.part_id,
    }));
  }],
];

// ---------------------------------------------------------------------------
// Dispatch
// ---------------------------------------------------------------------------

function matchPath(pattern, pathname) {
  const p = pattern.split("/");
  const s = pathname.split("/");
  if (p.length !== s.length) return null;
  const params = {};
  for (let i = 0; i < p.length; i++) {
    if (p[i].startsWith(":")) {
      params[p[i].slice(1)] = decodeURIComponent(s[i]);
    } else if (p[i] !== s[i]) {
      return null;
    }
  }
  return params;
}

/**
 * Drop-in replacement for the old `fetch(apiUrl + path)`.
 * Returns parsed JSON, or null on any failure — matching the old apiCall.
 */
export async function apiRoute(method, path, body) {
  const [pathname, search = ""] = String(path).split("?");
  const query = new URLSearchParams(search);
  const verb = String(method || "GET").toUpperCase();

  for (const [routeMethod, pattern, handler] of ROUTES) {
    if (routeMethod !== verb) continue;
    const params = matchPath(pattern, pathname);
    if (!params) continue;
    try {
      const result = await handler(params, query, body);
      return result === undefined ? null : result;
    } catch (err) {
      console.error(`[supabaseApi] ${verb} ${pathname} failed:`, err.message || err);
      return null;
    }
  }

  console.warn(`[supabaseApi] no route for ${verb} ${pathname}`);
  return null;
}

// ---------------------------------------------------------------------------
// Auth — replaces POST /api/auth/login
// ---------------------------------------------------------------------------

/**
 * The app has always used a single shared password, so the account email is a
 * fixed constant and the login screen still asks only for a password.
 */
export const SHARED_ACCOUNT_EMAIL =
  process.env.REACT_APP_RAZERGRID_EMAIL || "app@razergrid.local";

export async function signIn(password) {
  const { data, error } = await supabase.auth.signInWithPassword({
    email: SHARED_ACCOUNT_EMAIL,
    password,
  });
  if (error) {
    return { ok: false, error: error.message || "Incorrect password" };
  }
  // Best effort — a failed audit write must not block a valid login.
  try { await rpc("rg_log_login", { p_type: "shared" }); } catch { /* ignore */ }

  return {
    ok: true,
    token: data.session.access_token,
    expiresAt: new Date(data.session.expires_at * 1000).toISOString(),
  };
}

export async function signOut() {
  try { await supabase.auth.signOut(); } catch { /* ignore */ }
}

export async function getSessionToken() {
  const { data } = await supabase.auth.getSession();
  return data?.session?.access_token || null;
}

// ---------------------------------------------------------------------------
// Grading export — replaces GET /api/grading/export
//
// The server generated the workbook itself (a hand-rolled zip/XLSX writer).
// There is no server now, so the file is built in the browser. App.jsx already
// loads SheetJS from a CDN; CSV needs nothing.
// ---------------------------------------------------------------------------

const EXPORT_HEADERS = [
  "part_id", "grade", "reference_grade", "override_grade", "team",
  "description", "user_def16", "excluded", "silent_exclude", "updated_at",
];

async function gradingExportRows() {
  const rows = await selectAll("grading_reference", (q) => q.order("part_id"),
    "part_id,grade,description,team,user_def16,excluded,silent_exclude,updated_at");
  const overrides = await selectAll("grade_overrides", null, "part_id,grade,updated_at");
  const om = {};
  for (const o of overrides) om[o.part_id] = o;

  return rows.map((r) => {
    const override = om[r.part_id];
    return {
      part_id: r.part_id,
      grade: override ? override.grade : r.grade,
      reference_grade: r.grade || "",
      override_grade: override ? override.grade : "",
      team: r.team || "",
      description: r.description || "",
      user_def16: r.user_def16 || "",
      excluded: r.excluded ? 1 : 0,
      silent_exclude: r.silent_exclude ? 1 : 0,
      updated_at: override ? override.updated_at : r.updated_at,
    };
  });
}

function csvCell(value) {
  const s = value === null || value === undefined ? "" : String(value);
  return /[",\r\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
}

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function exportGrading(format) {
  try {
    const data = await gradingExportRows();
    const stamp = new Date().toISOString().slice(0, 10);
    const wantXlsx = format === "xlsx";

    // SheetJS is injected from a CDN at runtime; fall back to CSV if it has
    // not loaded rather than failing the export outright.
    if (wantXlsx && typeof window !== "undefined" && window.XLSX) {
      const rows = [EXPORT_HEADERS, ...data.map((r) => EXPORT_HEADERS.map((h) => r[h]))];
      const ws = window.XLSX.utils.aoa_to_sheet(rows);
      const wb = window.XLSX.utils.book_new();
      window.XLSX.utils.book_append_sheet(wb, ws, "Updated Grades");
      const out = window.XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const filename = `razergrid-updated-grades-${stamp}.xlsx`;
      triggerDownload(
        new Blob([out], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        filename
      );
      return { ok: true, filename };
    }

    const lines = [EXPORT_HEADERS.map(csvCell).join(",")];
    for (const r of data) lines.push(EXPORT_HEADERS.map((h) => csvCell(r[h])).join(","));
    const filename = `razergrid-updated-grades-${stamp}.csv`;
    // Leading BOM so Excel opens it as UTF-8, matching the old server response.
    triggerDownload(
      new Blob(["﻿" + lines.join("\r\n")], { type: "text/csv;charset=utf-8" }),
      filename
    );
    return { ok: true, filename };
  } catch (err) {
    console.error("[supabaseApi] grading export failed:", err.message || err);
    return null;
  }
}
